import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import setFieldValue from "@insite/client-framework/Store/Components/ContactUsForm/Handlers/SetFieldValue";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { ContactFormContext } from "@insite/content-library/Widgets/ContactUs/ContactForm";
import TextField, { TextFieldPresentationProps } from "@ucs/MobiusOverrides/TextField/TextField";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";

const enum fields {
    label = "label",
    hintText = "hintText",
    isRequired = "isRequired",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.label]: string;
        [fields.hintText]: string;
        [fields.isRequired]: boolean;
    };
}

const mapStateToProps = (state: ApplicationState) => ({
    companyName: state.components.contactUsForm.fieldValues["companyName"],
});

const mapDispatchToProps = {
    setFieldValue,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface CompanyNameStyles {
    companyNameTextField?: TextFieldPresentationProps;
}

export const companyNameStyles: CompanyNameStyles = {};

const styles = companyNameStyles;

const CompanyName: React.FC<Props> = ({ fields, companyName, setFieldValue }) => {
    const { label, hintText, isRequired } = fields;
    const contactFormContext = React.useContext(ContactFormContext);
    const [companyNameErrorMessage, setCompanyNameErrorMessage] = React.useState("");
    const [val, setVal] = React.useState(companyName || "");

    React.useEffect(() => {
        setVal(companyName || "");
    }, [companyName]);

    React.useEffect(() => {
        contactFormContext.validators.companyName = validateMessage;
        if (typeof companyName !== "undefined") {
            validateMessage();
        }
    }, [companyName]);

    React.useEffect(() => {
        contactFormContext.validators.companyName = validateMessage;
    }, [isRequired]);

    const validateMessage = () => {
        const errorMessage =
            isRequired && !companyName
                ? translate("Please enter your company name.")
                : isRequired && companyName!.length > 800
                ? translate("Maximum amount of characters is 800")
                : "";
        setCompanyNameErrorMessage(errorMessage);
        return !errorMessage;
    };

    const companyNameChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
        setVal(event.target.value);
        setFieldValue({ key: "companyName", value: event.target.value });
    };

    return (
        <TextField
            {...styles.companyNameTextField}
            label={label}
            required={isRequired}
            placeholder={hintText}
            value={val}
            error={companyNameErrorMessage}
            onChange={companyNameChangeHandler}
        />
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(CompanyName),
    definition: {
        group: "Contact Us",
        icon: "input-pipe",
        fieldDefinitions: [
            {
                name: fields.label,
                displayName: "Label",
                editorTemplate: "TextField",
                fieldType: "Translatable",
                defaultValue: "Company",
                isRequired: true,
            },
            {
                name: fields.hintText,
                displayName: "Hint Text",
                editorTemplate: "TextField",
                fieldType: "Translatable",
                defaultValue: "Type Your Company Name Here",
                isRequired: true,
            },
            {
                name: fields.isRequired,
                displayName: "Required Field",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
            },
        ],
    },
};

export default widgetModule;
